import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import locales from 'assets/locales.json';
import { variables } from 'libs/shared/utils/variables/variables';
import { catchError, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private platformId = inject(PLATFORM_ID);

  public translate = inject(TranslateService);
  public http = inject(HttpClient);

  private langKey = variables.languageCodeStorageKey;
  private defaultLang = variables.defaultLanguage;

  private _siteSupportedLanguages = signal<string[]>([]);
  public readonly siteSupportedLanguages = this._siteSupportedLanguages.asReadonly();

  setLanguage(lang: string) {
    const normalized = (lang ?? '').trim().toLowerCase();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.langKey, normalized);
    }

    this.translate.use(normalized);
  }

  getLanguage(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(this.langKey) ?? this.defaultLang;
    }

    const current = (this.translate.currentLang || '').trim().toLowerCase();
    if (current) {
      return current;
    }

    return this.defaultLang;
  }

  getDefaultLanguage(): string {
    return this.defaultLang;
  }

  isSupportedLanguage(lang: string): boolean {
    const normalized = (lang ?? '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    const supported = this._siteSupportedLanguages();
    if (supported.length > 0) {
      return supported.includes(normalized);
    }

    const supportedLangs = locales.locales ?? [];
    return supportedLangs.includes(normalized);
  }

  setSiteSupportedLanguages(languages: string[]): void {
    if (Array.isArray(languages) && languages.length > 0) {
      const normalized = Array.from(
        new Set(
          languages
            .map((code) => (typeof code === 'string' ? code.trim().toLowerCase() : ''))
            .filter((code) => code.length > 0),
        ),
      );

      this._siteSupportedLanguages.set(normalized);
      return;
    }

    this._siteSupportedLanguages.set([]);
  }

  getSupportedLanguages(): string[] {
    const supported = this._siteSupportedLanguages();
    if (supported.length > 0) {
      return supported;
    }

    return locales.locales ?? [];
  }

  getLanguageAndRegisterLocaleData() {
    let localeIsoCode = '';
    switch (this.getLanguage()) {
      case 'zh-cn':
      case 'zh-tw':
        localeIsoCode = 'zh';
        break;

      case 'dv':
        localeIsoCode = 'ar';
        break;
      case 'sm':
        localeIsoCode = 'en';
        break;

      default:
        localeIsoCode = this.getLanguage();
        break;
    }

    if (isPlatformBrowser(this.platformId)) {
      const localeUrl = `/assets/locales/${localeIsoCode}.json`;
      this.http
        .get(localeUrl)
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((module: any) => {
          if (module?.[localeIsoCode]) {
            registerLocaleData(module?.[localeIsoCode], localeIsoCode);
          }
        });
    }

    return localeIsoCode;
  }
}
